import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "@/App.css";

// Build isolation logic
const BUILD_TARGET = process.env.REACT_APP_BUILD_TARGET || 'BOTH'; // Options: 'ADMIN', 'MERCHANT', 'BOTH'

// Admin Components
import AdminLayout from "./layouts/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTest from "./pages/admin/AdminTest";
import MerchantManagement from "./pages/admin/MerchantManagement";
import ApiConfigsManagement from "./pages/admin/ApiConfigsManagement";
import KycRequests from "./pages/admin/KycRequests";
import RechargeRequests from "./pages/admin/RechargeRequests";
import ApiRequests from "./pages/admin/ApiRequests";
import BankMaster from "./pages/admin/BankMaster";
import ApiConfig from "./pages/admin/ApiConfig";
import AdminTickets from "./pages/admin/Tickets";
import LedgerReports from "./pages/admin/LedgerReports";
import AdminServicesReport from "./pages/admin/ServicesReport";
import DirectRecharge from "./pages/admin/DirectRecharge";
import WalletAdjustmentsReport from "./pages/admin/WalletAdjustmentsReport";
import QrSettlement from "./pages/admin/QrSettlement";
import AepsSettlement from "./pages/admin/AepsSettlement";
import AdminWithdrawals from "./pages/admin/Withdrawals";

// Merchant Components
import SimpleMerchantLayout from "./layouts/SimpleMerchantLayout";
import MuiLogin from "./pages/auth/MuiLogin";
import EnhancedDashboard from "./pages/merchant/EnhancedDashboard";
import KycDocuments from "./pages/merchant/KycDocuments";
import Wallet from "./pages/merchant/Wallet";
import Apis from "./pages/merchant/Apis";
import Developer from "./pages/merchant/Developer";
import Reports from "./pages/merchant/Reports";
import GatewayReports from "./pages/merchant/GatewayReports";
import ServicesReport from "./pages/merchant/ServicesReport";
import Support from "./pages/merchant/Support";
import Profile from "./pages/merchant/Profile";
import MerchantWithdrawals from "./pages/merchant/Withdrawals";

// Legal Pages
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import RefundPolicy from "./pages/legal/RefundPolicy";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to={user.role === "ADMIN" ? "/admin" : "/merchant"} replace />;
  }

  return children;
};

// Redirect if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === "ADMIN" ? "/admin" : "/merchant"} replace />;
  }

  return children;
};

function App() {
  const theme = createTheme({
    palette: {
      primary: {
        main: '#10b981',
      },
      secondary: {
        main: '#6366f1',
      },
      background: {
        default: '#f8fafc',
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: 12,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* -- Admin Route Target -- */}
          {(BUILD_TARGET === 'ADMIN' || BUILD_TARGET === 'BOTH') && (
            <>
              {/* Admin Login Route */}
              <Route path="/admin/login" element={
                <AdminAuthProvider>
                  <AdminLogin />
                </AdminAuthProvider>
              } />

              {/* Admin Protected Routes */}
              <Route path="/admin" element={
                <AdminAuthProvider>
                  <AdminLayout />
                </AdminAuthProvider>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="merchants" element={<MerchantManagement />} />
                <Route path="api-configs" element={<ApiConfigsManagement />} />
                <Route path="kyc" element={<KycRequests />} />
                <Route path="recharges" element={<RechargeRequests />} />
                <Route path="direct-recharge" element={<DirectRecharge />} />
                <Route path="api-requests" element={<ApiRequests />} />
                <Route path="banks" element={<BankMaster />} />
                <Route path="apis" element={<ApiConfig />} />
                <Route path="tickets" element={<AdminTickets />} />
                <Route path="ledger" element={<LedgerReports />} />
                <Route path="wallet-adjustments" element={<WalletAdjustmentsReport />} />
                <Route path="qr-settlement" element={<QrSettlement />} />
                <Route path="aeps-settlement" element={<AepsSettlement />} />
                <Route path="services-report" element={<AdminServicesReport />} />
                <Route path="withdrawals" element={<AdminWithdrawals />} />
              </Route>
            </>
          )}

          {/* -- Merchant & Public Route Target -- */}
          {(BUILD_TARGET === 'MERCHANT' || BUILD_TARGET === 'BOTH') && (
            <>
              {/* Public Login Route */}
              <Route path="/login" element={
                <AuthProvider>
                  <PublicRoute><MuiLogin /></PublicRoute>
                </AuthProvider>
              } />

              {/* Legal Pages (public) */}
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />

              {/* Merchant Protected Routes */}
              <Route path="/merchant" element={
                <AuthProvider>
                  <ProtectedRoute allowedRole="MERCHANT">
                    <SimpleMerchantLayout />
                  </ProtectedRoute>
                </AuthProvider>
              }>
                <Route index element={<EnhancedDashboard />} />
                <Route path="kyc" element={<KycDocuments />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="apis" element={<Apis />} />
                <Route path="developer" element={<Developer />} />
                <Route path="reports" element={<Reports />} />
                <Route path="gateway-reports" element={<GatewayReports />} />
                <Route path="services-report" element={<ServicesReport />} />
                <Route path="support" element={<Support />} />
                <Route path="profile" element={<Profile />} />
                <Route path="withdrawals" element={<MerchantWithdrawals />} />
              </Route>
            </>
          )}

          {/* Default redirects */}
          <Route path="/" element={<Navigate to={BUILD_TARGET === 'ADMIN' ? "/admin/login" : "/login"} replace />} />
          <Route path="*" element={<Navigate to={BUILD_TARGET === 'ADMIN' ? "/admin/login" : "/login"} replace />} />
        </Routes>
        <ToastContainer position="top-right" richColors />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
